// src/pages/Invoices/Report/ZatcaReport.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography, Tooltip, CircularProgress, Alert } from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleSendOutlinedIcon from "@mui/icons-material/ScheduleSendOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import axios from "../../../axios";
import invoiceApi from "../../../api/invoiceApi";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, reportGridSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

const STATUS_OPTIONS = [
  { value: "REPORTED", label: "تم الإبلاغ (Reported)" },
  { value: "CLEARED", label: "تم الاعتماد (Cleared)" },
  { value: "FAILED", label: "فشل (Failed)" },
  { value: "Not Sent", label: "لم يُرسل" },
];

const STATUS_STYLE = {
  REPORTED: { label: "تم الإبلاغ", bg: "#d1fae5", fg: "#047857" },
  CLEARED: { label: "معتمدة", bg: "#d1fae5", fg: "#047857" },
  FAILED: { label: "فشل", bg: "#ffe4e6", fg: "#be123c" },
  "Not Sent": { label: "لم يُرسل", bg: "#f1f5f9", fg: "#475569" },
};

// منع حقن HTML من نصوص الأخطاء القادمة من الهيئة داخل نافذة التفاصيل
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export default function ZatcaReport() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  // ✅ تاريخ اليوم بالتوقيت المحلي (سابقًا toISOString كان يعطي تاريخ أمس بعد منتصف الليل)
  const range = useReportDateRange("today");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  // حالة الربط (المرحلة الثانية) من إعدادات الربط: null = جاري التحقق
  const [linkActive, setLinkActive] = useState(null);
  // تاريخ ووقت الربط: لا تظهر إلا الفواتير المُنشأة بعده (التصفية في الخادم)
  const [linkedAt, setLinkedAt] = useState(null);

  useEffect(() => {
    axios.get("/ZatcaSettings")
      .then(r => {
        setLinkActive(!!r.data?.isActive);
        setLinkedAt(r.data?.activatedAt || null);
      })
      .catch(() => setLinkActive(false));
  }, []);

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get("/ZatcaReport", {
        params: {
          companyId,
          fromDate: range.dateFrom,
          toDate: range.dateTo,
          status: status || "All",
        },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, range.dateFrom, range.dateTo, status]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleResend = useCallback(async (item) => {
    if (sendingId) return;                       // إرسال واحد في كل مرة
    const ok = await Swal.fire({
      title: "إرسال الفاتورة للهيئة",
      html: `سيتم إرسال الفاتورة <b dir="ltr">${esc(item.invoiceNumber)}</b> إلى هيئة الزكاة والضريبة.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "إرسال",
      cancelButtonText: "إلغاء",
    });
    if (!ok.isConfirmed) return;

    setSendingId(item.id);
    try {
      await invoiceApi.approve(item.id);
      toast.success("تم إرسال الفاتورة للهيئة بنجاح ✅");
    } catch (e) {
      const d = e?.response?.data;
      toast.error(typeof d === "string" ? d : d?.message || "فشل إرسال الفاتورة للهيئة ❌");
    } finally {
      setSendingId(null);
      fetchReport();                             // نعرض الحالة الفعلية من الخادم في الحالتين
    }
  }, [fetchReport, sendingId]);

  const showDetails = (item) => {
    Swal.fire({
      title: "تفاصيل استجابة الهيئة",
      html: `
        <div style="text-align:left; direction:ltr; max-height:340px; overflow:auto; font-size:13px;">
          <p><strong>Status:</strong> ${esc(item.zatcaStatus)}</p>
          <p><strong>UUID:</strong> ${esc(item.zatcaUuid || "N/A")}</p>
          <p style="word-break:break-all"><strong>Hash:</strong> ${esc(item.zatcaHash || "N/A")}</p>
          <hr/>
          <strong>Warnings:</strong>
          <pre style="white-space:pre-wrap">${esc(item.warnings || "None")}</pre>
          <hr/>
          <strong>Errors:</strong>
          <pre style="white-space:pre-wrap; color:#be123c">${esc(item.errors || "None")}</pre>
        </div>`,
      width: 640,
      confirmButtonText: "إغلاق",
    });
  };

  const stats = useMemo(() => {
    const count = (s) => rows.filter(r => r.zatcaStatus === s).length;
    return {
      total: rows.length,
      ok: count("REPORTED") + count("CLEARED"),
      failed: count("FAILED"),
      notSent: count("Not Sent"),
      amount: rows.reduce((s, r) => s + (Number(r.totalAmount) || 0), 0),
    };
  }, [rows]);

  const columns = useMemo(() => [
    {
      field: "invoiceNumber", headerName: "رقم الفاتورة", minWidth: 120, flex: .8, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 800, color: "#4f46e5", direction: "ltr", fontSize: 14 }}>{p.value}</Typography>,
    },
    {
      field: "invoiceDate", headerName: "التاريخ", minWidth: 130, flex: .9, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.25, textAlign: "center" }}>
          <Typography sx={{ direction: "ltr", fontSize: 14 }}>{p.value}</Typography>
          {p.row.createdAt && (
            <Typography sx={{ direction: "ltr", fontSize: 11.5, color: "#94a3b8" }}>
              {new Date(p.row.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </Typography>
          )}
        </Box>
      ),
    },
    { field: "customerName", headerName: "العميل", minWidth: 160, flex: 1.3, headerAlign: "center", align: "center" },
    {
      field: "totalAmount", headerName: "الإجمالي", type: "number", minWidth: 120, flex: .8, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 14 }}>{fmtMoney(p.value)}</Typography>,
    },
    {
      field: "zatcaStatus", headerName: "الحالة", minWidth: 120, flex: .8, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const s = STATUS_STYLE[p.value] ?? { label: p.value, bg: "#f1f5f9", fg: "#475569" };
        return (
          <Box sx={{ px: 1.25, py: .35, borderRadius: "999px", fontSize: 12.5, fontWeight: 800, bgcolor: s.bg, color: s.fg }}>
            {s.label}
          </Box>
        );
      },
    },
    {
      field: "reportedAt", headerName: "وقت الإرسال", minWidth: 150, flex: 1, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Typography sx={{ direction: "ltr", fontSize: 13, color: "#475569" }}>
          {p.value ? new Date(p.value).toLocaleString("en-GB") : "—"}
        </Typography>
      ),
    },
    {
      field: "zatcaUuid", headerName: "UUID", minWidth: 110, flex: .7, headerAlign: "center", align: "center",
      renderCell: (p) => p.value ? (
        <Tooltip title={p.value}>
          <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#64748b" }}>{p.value.substring(0, 8)}…</Typography>
        </Tooltip>
      ) : "—",
    },
    {
      field: "actions", headerName: "الإجراءات", minWidth: 220, flex: 1.3, sortable: false, filterable: false,
      headerAlign: "center", align: "center",
      renderCell: (p) => {
        const item = p.row;
        // ✅ الفاتورة المقبولة (مُبلَّغة/معتمدة) لا تُرسل مرة أخرى، ولا إرسال والربط غير نشط
        const accepted = item.zatcaStatus === "REPORTED" || item.zatcaStatus === "CLEARED";
        const canSend = !accepted && (item.zatcaStatus === "Not Sent" || item.zatcaStatus === "FAILED");
        const sending = sendingId === item.id;
        const blocked = !linkActive || (!!sendingId && !sending);
        return (
          <Box sx={{ display: "flex", gap: .75 }}>
            {item.zatcaStatus !== "Not Sent" && (
              <Button size="small" variant="outlined" startIcon={<InfoOutlinedIcon />} onClick={() => showDetails(item)}
                sx={{ borderRadius: "9px", fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>
                تفاصيل
              </Button>
            )}
            {canSend && (
              <Tooltip title={!linkActive ? "الربط مع الهيئة غير نشط" : ""}>
              <span>
              <Button size="small" variant="contained" disableElevation disabled={sending || blocked}
                startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ transform: "scaleX(-1)" }} />}
                onClick={() => handleResend(item)}
                sx={{ borderRadius: "9px", fontWeight: 700, bgcolor: "#4f46e5", '&:hover': { bgcolor: "#4338ca" }, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>
                {sending ? "جاري الإرسال" : item.zatcaStatus === "FAILED" ? "إعادة الإرسال" : "إرسال للهيئة"}
              </Button>
              </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [sendingId, handleResend, linkActive]);

  return (
    <ReportPage
      title="تقرير الفوترة الإلكترونية"
      subtitle="حالة إرسال الفواتير إلى هيئة الزكاة والضريبة والجمارك (ZATCA)"
      icon={<VerifiedOutlinedIcon />}
      period={range}
      actions={[{ label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading, primary: true }]}
      stats={[
        { label: "عدد الفواتير", value: fmtNumber(stats.total), icon: <ReceiptLongIcon />, color: "primary" },
        { label: "مُرسلة بنجاح", value: fmtNumber(stats.ok), icon: <TaskAltIcon />, color: "success" },
        { label: "فشلت", value: fmtNumber(stats.failed), icon: <ErrorOutlineIcon />, color: "error" },
        { label: "لم تُرسل", value: fmtNumber(stats.notSent), icon: <ScheduleSendOutlinedIcon />, color: "warning" },
        { label: "إجمالي المبالغ", value: fmtMoney(stats.amount), icon: <VerifiedOutlinedIcon />, color: "info", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          onApply={fetchReport}
          onReset={() => { range.reset(); setStatus(""); }}
          loading={loading}
          activeCount={status ? 1 : 0}
        >
          <ReportSelect label="حالة الإرسال" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        </ReportFilters>
      }
    >
      {linkActive === false && (
        <Alert severity="warning" icon={<LinkOffIcon />} sx={{ mb: 1.5, borderRadius: "12px" }}>
          الربط مع الهيئة (المرحلة الثانية) <b>غير نشط</b> — لا يمكن إرسال الفواتير. فعّله من إعدادات الربط بعد الحصول على شهادة الإنتاج.
        </Alert>
      )}
      {linkedAt && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: "12px" }}>
          تظهر فقط الفواتير المُنشأة بعد تاريخ الربط مع الهيئة:{" "}
          <b style={{ direction: "ltr", unicodeBidi: "embed" }}>{new Date(linkedAt).toLocaleString("en-GB")}</b>
        </Alert>
      )}
      <ReportTableCard title="الفواتير" count={fmtNumber(rows.length)}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          getRowHeight={() => 56}
          localeText={{ noRowsLabel: "لا توجد فواتير في هذه الفترة" }}
          sx={reportGridSx}
        />
      </ReportTableCard>
    </ReportPage>
  );
}